import { IItemResponseWithSuccess } from '@common/api.models';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IPagedResultsV1 } from '@gcl/react-core';
import { IProjectRequest, IProjectRequestSearchFilter, IUpsertProjectRequestRequest } from './projectRequest.models';
import {
  getProjectRequestCompaniesAsync,
  getProjectRequestDefaultCompaniesAsync,
  getProjectRequestsSearchAsync,
  getProjectRequestsDetailAsync,
  getProjectRequestTeamsAsync,
  getProjectRequestUsersAsync,
  saveProjectRequestAsDraftAsync,
  requestProjectRequestEndorsementAsync,
  endorseAndSubmitProjectRequestAsync,
  approveProjectRequestAndCreateProjectAsync,
} from './projectRequest.services';
import { IProjectRequestCompany } from './projectRequestCompany/projectRequestCompany.models';
import { IProjectRequestTeam } from './projectRequestTeam/projectRequestTeam.models';
import { IProjectRequestUser } from './projectRequestUser/projectRequestUser.models';

export const useFetchProjectRequestSearch = (props: IProjectRequestSearchFilter) => {
  return useQuery<IPagedResultsV1<IProjectRequest> | undefined, AxiosErrorResponse>(
    [props, FETCH_QUERIES_NAME.PROJECT_REQUEST_BY_PROFILE_ID],
    async () => {
      if (!props.profileId) {
        return undefined;
      }
      const results = await getProjectRequestsSearchAsync(props);
      return results;
    },
    {
      enabled: isPositiveNumber(props.profileId),
    },
  );
};

export const useFetchProjectRequestDetails = (id?: number, enabled = true) => {
  return useQuery<IProjectRequest | undefined, AxiosErrorResponse>(
    [id, FETCH_QUERIES_NAME.PROJECT_REQUEST_DETAIL],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getProjectRequestsDetailAsync(id);
      return results.item;
    },
    {
      enabled: isPositiveNumber(id) && enabled,
    },
  );
};

export const useFetchProjectRequestCompanies = (id?: number, enabled = true) => {
  return useQuery<IProjectRequestCompany[] | undefined, AxiosErrorResponse>(
    [id, FETCH_QUERIES_NAME.PROJECT_REQUEST_DETAIL_COMPANIES],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getProjectRequestCompaniesAsync(id);
      return results.items;
    },
    {
      enabled: isPositiveNumber(id) && enabled,
    },
  );
};

export const useFetchProjectRequestTeams = (id?: number, enabled = true) => {
  return useQuery<IProjectRequestTeam[] | undefined, AxiosErrorResponse>(
    [id, FETCH_QUERIES_NAME.PROJECT_REQUEST_DETAIL_TEAMS],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getProjectRequestTeamsAsync(id);
      return results.items;
    },
    {
      enabled: isPositiveNumber(id) && enabled,
    },
  );
};

export const useFetchProjectRequestUsers = (id?: number, enabled = true) => {
  return useQuery<IProjectRequestUser[] | undefined, AxiosErrorResponse>(
    [id, FETCH_QUERIES_NAME.PROJECT_REQUEST_DETAIL_USERS],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getProjectRequestUsersAsync(id);
      return results.items;
    },
    {
      enabled: isPositiveNumber(id) && enabled,
    },
  );
};

export const useFetchProjectRequestDefaultCompanies = (isEnabled = true) => {
  return useQuery(
    [FETCH_QUERIES_NAME.PROJECT_REQUEST_DEFAULT_COMPANIES],
    async () => {
      const results = await getProjectRequestDefaultCompaniesAsync();
      return results.items;
    },
    {
      enabled: isEnabled,
    },
  );
};

export const useSaveProjectRequestAsDraft = () => {
  return useMutation<IItemResponseWithSuccess<IProjectRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.SAVE_PROJECT_REQUEST_AS_DRAFT],
    async (request: IUpsertProjectRequestRequest) => {
      const result = await saveProjectRequestAsDraftAsync(request);
      return result;
    },
  );
};

export const useRequestProjectRequestEndorsement = () => {
  return useMutation<IItemResponseWithSuccess<IProjectRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.REQUEST_PROJECT_REQUEST_ENDORSEMENT],
    async (request: IUpsertProjectRequestRequest) => {
      const result = await requestProjectRequestEndorsementAsync(request);
      return result;
    },
  );
};

export const useEndorseAndSubmitProjectRequest = () => {
  return useMutation<IItemResponseWithSuccess<IProjectRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.ENDORSE_AND_SUBMIT_PROJECT_REQUEST],
    async (request: IUpsertProjectRequestRequest) => {
      const result = await endorseAndSubmitProjectRequestAsync(request);
      return result;
    },
  );
};

export const useApproveProjectRequestAndCreateProject = () => {
  return useMutation<IItemResponseWithSuccess<IProjectRequest>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.APPROVE_PROJECT_REQUEST_AND_CREATE_PROJECT],
    async (request: IUpsertProjectRequestRequest) => {
      const result = await approveProjectRequestAndCreateProjectAsync(request);
      return result;
    },
  );
};
